/**
 * Normalize paper-journal upload API errors (FastAPI `detail` string or object).
 */

export interface PaperJournalUploadErrorPayload {
  status: number | null;
  message: string;
  fileSha256?: string;
  existingUploadId?: number | null;
}

function _detailMessage(detail: unknown): string | null {
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'object' && item !== null && 'msg' in item) {
          const msg = (item as { msg: unknown }).msg;
          return typeof msg === 'string' ? msg : null;
        }
        return null;
      })
      .filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join('; ') : null;
  }
  if (typeof detail === 'object' && detail !== null && 'message' in detail) {
    const msg = (detail as { message: unknown }).message;
    return typeof msg === 'string' ? msg : null;
  }
  return null;
}

function _detailObjectFields(
  detail: unknown,
): Pick<PaperJournalUploadErrorPayload, 'fileSha256' | 'existingUploadId'> {
  if (typeof detail !== 'object' || detail === null || Array.isArray(detail)) {
    return {};
  }
  const obj = detail as Record<string, unknown>;
  const fileSha256 = typeof obj.file_sha256 === 'string' ? obj.file_sha256 : undefined;
  let existingUploadId: number | null | undefined;
  if ('existing_upload_id' in obj) {
    existingUploadId =
      typeof obj.existing_upload_id === 'number' ? obj.existing_upload_id : null;
  }
  return { fileSha256, existingUploadId };
}

/** Extract upload failure info from an axios-style error. */
export function extractPaperJournalUploadError(error: unknown): PaperJournalUploadErrorPayload {
  if (typeof error === 'object' && error !== null) {
    const err = error as {
      response?: { status?: number; data?: { detail?: unknown; message?: unknown } };
      message?: string;
    };
    const status = err.response?.status ?? null;
    const rawDetail = err.response?.data?.detail ?? err.response?.data?.message;
    const parsedMessage = _detailMessage(rawDetail);
    if (parsedMessage) {
      return {
        status,
        message: parsedMessage,
        ..._detailObjectFields(rawDetail),
      };
    }
    if (err.message) {
      return { status, message: err.message };
    }
  }
  return { status: null, message: 'Upload failed' };
}
